import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { CacheService, PlatformAuthService, PlatformBrowserService, PlatformInfoService } from '@tgx/shared/services';
import { combineLatest, filter, startWith, switchMap, take } from 'rxjs';
import { ProfileCache, SelectorOrganization, UserData } from '@tgx/shared/interfaces';
import { getOnboardingRoutingDecision } from '@tgx/shared/utils';

/**
 * The member (personal) onboarding is considered finished when there are no onboarding
 * properties yet, or when they exist and `finishedAt` is set. Mirrors the check in
 * AppComponent so the decision stays consistent across guard and component.
 */
const isMemberOnboardingFinished = (userData: UserData | null | undefined): boolean =>
  !userData?.onboardingProperties || userData.onboardingProperties.finishedAt !== null;

/**
 * This guard protects main application routes (e.g., /dashboard) and is the single
 * source of truth for onboarding-based redirects.
 *
 * Priority is deterministic and independent of bootstrap/hydration timing:
 * 1. If the member (personal) onboarding is NOT finished -> /onboarding-company.
 * 2. Else, if the organization onboarding is pending -> /onboarding-implementation.
 * 3. Else, allow access.
 *
 * Member onboarding must win over org onboarding. To avoid deciding with a stale user
 * (e.g. the base admin replayed by `userLogged$` while impersonation is still settling),
 * the guard waits for the impersonated member's emission (`isImpersonated === true`) when
 * impersonating; otherwise it takes the first available user. No extra network calls are
 * made: on normal navigation the user is already cached and resolves instantly; it only
 * waits during the impersonation transition, which is exactly the moment it must.
 */
export const requireOnboardingGuard: CanActivateFn = () => {
  const router = inject(Router);
  const cacheService = inject(CacheService);
  const platformInfoService = inject(PlatformInfoService);
  const platformBrowserService = inject(PlatformBrowserService);
  const platformAuthService = inject(PlatformAuthService);

  if (!platformInfoService.isBrowser()) {
    return true;
  }

  // While impersonating, the decision must use the impersonated member's data, not the
  // base admin that `userLogged$` may still be replaying during the impersonation transition.
  const isImpersonating = !!platformAuthService.getCurrentImpersonationEmail();

  return combineLatest([
    platformInfoService.organizationSelected$.pipe(startWith(platformInfoService.selectedOrg ?? null)),
    platformInfoService.userLogged$,
  ]).pipe(
    filter(([, userData]) => !!userData && (!isImpersonating || userData.isImpersonated === true)),
    take(1),
    switchMap(async ([org, userData]: [SelectorOrganization | null, UserData]): Promise<boolean | UrlTree> => {
      // 1. Member (personal) onboarding takes priority over the organization onboarding.
      if (!isMemberOnboardingFinished(userData)) {
        console.info('Guard 🚫: Member onboarding pending. Redirecting to /onboarding-company.');
        return router.createUrlTree(['/onboarding-company']);
      }

      // During organization switch, storage is updated before route guard execution.
      // Prioritize persisted org/type to avoid evaluating with a stale in-memory emission.
      const orgCode = platformBrowserService.getLocalStorageItem('organizationCode') || org?.code;
      const typeSelected = platformBrowserService.getLocalStorageItem('organizationType') || org?.typeSelected;

      // During initial bootstrap org can still be unresolved; do not block navigation.
      if (!orgCode) {
        return true;
      }

      const profile: ProfileCache = (await cacheService.retrieveProfilesByCodes([orgCode]))[0];

      if (!profile) {
        return true;
      }

      const decision = getOnboardingRoutingDecision(profile, typeSelected);

      if (!decision.shouldRedirect) {
        console.info(`Guard🚦: Onboarding access allowed for organization ${orgCode}.`);
        return true;
      }

      console.info(
        `Guard 🚫: Onboarding pending for organization ${orgCode} (reason: ${decision.reason}). Redirecting to ${decision.targetUrl}.`,
      );
      return router.createUrlTree([decision.targetUrl]);
    }),
  );
};
